import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Field, Select, Status } from "@allergy123/ui";
import { useState } from "react";

/**
 * Invite a person (ADR 0010): the API creates the identity, the membership or
 * direct entitlement, and the pathway enrollments; Cognito emails a temporary
 * password and first sign-in happens through managed login.
 */
export function InvitePage() {
  const api = useApi();
  const refs = useLoad((a) => a.admin.invitationReferences());
  const [form, setForm] = useState({ email: "", givenName: "", familyName: "", organizationId: "", role: "member", pathwayId: "", direct: false });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.type === "checkbox" ? (e.target as HTMLInputElement).checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };
  const selectedOrg = refs.data?.organizations.find((o) => o.id === form.organizationId);
  const needsAccess = Boolean(selectedOrg && !selectedOrg.products.includes("learn") && !form.direct);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      const res = await api.admin.invite({
        email: form.email.trim(),
        givenName: form.givenName.trim() || undefined,
        familyName: form.familyName.trim() || undefined,
        organizationId: form.organizationId || undefined,
        role: form.role as "owner" | "admin" | "member",
        products: form.direct || !form.organizationId ? ["learn"] : [],
        pathwayId: form.pathwayId || undefined,
      });
      setNote({ kind: "success", text: `${res.status === "invited" ? "Invited" : "Updated"} ${form.email}; ${res.enrollmentsCreated} module${res.enrollmentsCreated === 1 ? "" : "s"} enrolled.` });
      setForm((f) => ({ ...f, email: "", givenName: "", familyName: "" }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setNote({ kind: "error", text: message.includes("learning_access_required")
        ? "Grant learning access to the practice on Organizations, or select a direct learning entitlement below."
        : message.includes("membership_inactive")
          ? "This person's membership in the practice is suspended or ended. Reinstate them on the practice's Members page instead."
          : `Couldn't invite (${message || "error"}).` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className="mb-2 text-h1 font-semibold text-anchor">Invite a person</h1>
      <p className="mb-8 max-w-[68ch] text-ink-secondary">They receive a temporary password by email and set their own on first sign-in. Membership in a practice gives them that practice's product access; an individual learner gets a direct entitlement instead.</p>
      <Card className="max-w-[640px]">
        {refs.state === "error" && <div className="mb-5"><Status kind="error" label={`Couldn't load practices and pathways (${refs.error?.message}).`} /><Button variant="secondary" onClick={refs.reload}>Retry</Button></div>}
        {refs.state === "loading" && <p className="mb-5">Loading practices and pathways…</p>}
        <form onSubmit={(e) => void submit(e)} className="grid gap-5">
          <Field label="Email" type="email" value={form.email} onChange={set("email")} required wide />
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="First name" value={form.givenName} onChange={set("givenName")} wide />
            <Field label="Last name" value={form.familyName} onChange={set("familyName")} wide />
          </div>
          <Select
            label="Practice"
            value={form.organizationId}
            onChange={set("organizationId")}
            placeholder="None — individual learner"
            options={(refs.data?.organizations ?? []).filter((o) => o.status === "active").map((o) => ({ value: o.id, label: o.name }))}
            hint="Leave empty for someone with no practice; they get a direct learning entitlement."
          />
          {form.organizationId && (
            <Select label="Role in the practice" value={form.role} onChange={set("role")} options={[{ value: "member", label: "Member" }, { value: "admin", label: "Admin (can assign training)" }, { value: "owner", label: "Owner" }]} />
          )}
          {needsAccess && <Status kind="warning" label="This practice has no learning access. Grant it on Organizations, or select a direct entitlement below." />}
          <Select label="Pathway to assign" value={form.pathwayId} onChange={set("pathwayId")} placeholder="None yet" options={(refs.data?.pathways ?? []).map((p) => ({ value: p.id, label: p.title }))} />
          {form.organizationId && (
            <label className="flex items-center gap-2 text-body">
              <input type="checkbox" checked={form.direct} onChange={set("direct")} /> Also grant a direct learning entitlement
            </label>
          )}
          {note && <Status kind={note.kind} label={note.text} />}
          <div>
            <Button type="submit" disabled={busy || !form.email || refs.state !== "ready" || needsAccess}>
              {busy ? "Inviting…" : "Send invitation"}
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
}
